// 2026-07-02 US-003: bulk status notifications page — real container list, real recipient
// retrieval (container OR reference-number, mutually exclusive, all-or-nothing validation
// in Reference mode), and real sending (endpoint re-resolves + re-projects recipients,
// dedupes by contact, sends synchronously within the request; see onConfirmSend). The
// response carries the real final Sent/Failed counts — no polling needed. "View sent
// messages" is where the full per-recipient audit trail for any past send lives.
import { Button, Card, Checkbox, Col, Form, Input, Radio, Row, Select, Space, Table, Typography, Upload } from 'antd';
import { CheckCircleOutlined, SendOutlined, UploadOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Constants, Roles } from 'common/constants';
import {
  NotifChannel,
  RecipientMode,
  ResolvedChannel,
  SendRequest,
  SendStatus,
} from 'common/models/BulkNotificationTypes';
import { ConfirmationModal } from '@components/Custom/ConfirmationModal/ConfirmationModal';
import { CheckUserAccessState } from 'common/utils/utility';
import { PATHS } from '@routing/routes';
// 2026-07-02 US-003: real container list — Container/GetAll already orders by
// CreatedDate desc server-side (see ContainerDao.GetAll).
import { ContainerSelector, GetAllContainer } from '@features/Container';
import { AccountSelector } from '@features/Account';
import { OperationStatus } from 'store/rootTypes';
import { GetBulkNotificationRecipients, SendBulkNotification } from '../business/BulkNotificationBusiness';
import { BulkNotificationsActions } from '../store/BulkNotificationsSlice';
import { BulkNotificationsSelector } from '../store/BulkNotificationsSelector';
import { parseRefs, projectRecipients } from '../logic/resolveRecipients';
import '../bulkNotifications.css';

const { Text } = Typography;

const DEFAULT_EMAIL_SUBJECT = 'KFS Shipment Update';
const DEFAULT_EMAIL_BODY = [
  'Dear [Customer Name],',
  '',
  '[status update text]',
  '',
  "We'll update your tracking as soon as [next step].",
  '',
  'Thank you for your continued trust in KFS.',
  'Warm regards,',
  'KFS Balikbayan Box',
].join('\n');
const DEFAULT_SMS_BODY = 'KFS Update: [short status]. Track your box: [link]. Thank you. -KFS';

// 2026-07-02 US-003: plain running character count (includes spaces) shown below
// Subject/Email/SMS fields so ops can see how much they've typed as they go.
const charCountLabel = (value: string) =>
  `${value.length} character${value.length === 1 ? '' : 's'} (including spaces)`;

export const BulkNotifications = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const containerState = useSelector(ContainerSelector);
  const bulkState = useSelector(BulkNotificationsSelector);
  const accountState = useSelector(AccountSelector);

  // 2026-07-02 US-003: menu-level gate (Home.tsx) is UX only — this closes the
  // direct-navigation gap flagged in the security review (US-003 finding, HIGH) so typing
  // the URL doesn't bypass authorization. Server-side authorization on every endpoint (see
  // BulkNotificationService's per-method access check) is the real control; this is
  // defense-in-depth for the UI. Access: Admin role, or the AllowedEmailsInStates
  // allowlist (mayette/erick/manny/michelle@kfs.com), same as the Home.tsx menu gate.
  useEffect(() => {
    if (accountState.RoleName !== Roles.Admin && !CheckUserAccessState(accountState.Username, accountState.RoleName)) {
      history.push(PATHS.Home.path);
    }
  }, [accountState.RoleName, accountState.Username]);

  const [mode, setMode] = useState<RecipientMode>(RecipientMode.Container);
  const [containerId, setContainerId] = useState<string | undefined>(undefined);
  const [refsText, setRefsText] = useState('');
  const [channel, setChannel] = useState<NotifChannel>(NotifChannel.Email);
  // 2026-07-02 US-003: fields start empty — the standard templates (AC-21) are shown
  // as placeholder guidance only, not pre-filled, so ops must actively compose the message.
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [smsBody, setSmsBody] = useState('');
  // 2026-07-02 US-003: fallback defaults OFF regardless of channel — ops must
  // deliberately opt in to cross-channel sending for either direction.
  const [fallbackOn, setFallbackOn] = useState(false);
  // 2026-07-02 US-003: optional PDF attachment, Email-only. Read client-side as a quick
  // check, but the backend independently re-validates (extension, magic bytes, size) —
  // this is UX only, not the real control.
  const [attachmentFileName, setAttachmentFileName] = useState<string | undefined>(undefined);
  const [attachmentBase64, setAttachmentBase64] = useState<string | undefined>(undefined);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  // 2026-07-02 US-003: covers the Send request (synchronous — resolves once sending is
  // fully done). Passed to ConfirmationModal's loadingState, which disables both
  // Send/Cancel — the double-submit guard the earlier security review flagged as missing.
  const [isSending, setIsSending] = useState(false);
  // 2026-07-02 US-003: the outcome of the last completed send, shown in a modal that must
  // be explicitly acknowledged (OK) so it can't be missed like a transient toast would be.
  const [sendResult, setSendResult] = useState<
    { status: SendStatus; sentCount: number; failedCount: number; totalRecipients: number } | undefined
  >(undefined);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  // 2026-07-02 US-003: audience must be validated via "Check recipients" (a real API call)
  // before compose/send; editing the audience clears this so a send can never run against
  // an unvalidated or stale set.
  const [checked, setChecked] = useState(false);
  // 2026-07-02 US-003: field-level "required" errors render inline below each control
  // (Constants.REQUIRED_FIELD), matching the app's Form.Item convention, instead of a
  // single generic message at the bottom. Set true on the first Preview attempt; each
  // field's error clears itself live once that field is no longer empty.
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  // 2026-07-02 US-003: same inline-below-the-control pattern for the audience fields,
  // triggered on "Check recipients" instead of "Preview and send".
  const [showAudienceError, setShowAudienceError] = useState(false);

  useEffect(() => {
    if (!containerState.ContainerList || containerState.ContainerList.length === 0) {
      dispatch(GetAllContainer());
    }
  }, []);

  const isEmail = channel === NotifChannel.Email;
  const isContainerMode = mode === RecipientMode.Container;
  const showEmailFields = isEmail || fallbackOn;
  const showSmsField = !isEmail || fallbackOn;
  const subjectMissing = showFieldErrors && showEmailFields && !subject.trim();
  const emailBodyMissing = showFieldErrors && showEmailFields && !emailBody.trim();
  const smsBodyMissing = showFieldErrors && showSmsField && !smsBody.trim();
  const containerMissing = showAudienceError && isContainerMode && !containerId;
  const refsMissing = showAudienceError && !isContainerMode && parseRefs(refsText).length === 0;
  const fallbackLabel = isEmail
    ? 'For customers without an email, send via SMS instead'
    : 'For customers without a mobile, send via email instead';

  const recipients = bulkState.RecipientsResponse?.Recipients || [];
  const result = useMemo(() => projectRecipients(recipients, channel, fallbackOn), [recipients, channel, fallbackOn]);

  const clearError = () => setError('');

  // Editing the audience invalidates a prior check — user must re-check before composing/sending.
  const onAudienceChange = () => {
    setChecked(false);
    clearError();
    dispatch(BulkNotificationsActions.SetRecipientsResponse(undefined));
  };

  const onModeChange = (value: RecipientMode) => {
    setMode(value);
    setContainerId(undefined);
    setRefsText('');
    onAudienceChange();
  };

  const columns = [
    { title: 'Ref no.', dataIndex: 'RefNo', key: 'RefNo' },
    { title: 'Customer', dataIndex: 'CustomerName', key: 'CustomerName' },
    {
      title: 'Email',
      dataIndex: 'Email',
      key: 'Email',
      render: (value: string | null) => value || '— (no email)',
    },
    {
      title: 'Mobile',
      dataIndex: 'Mobile',
      key: 'Mobile',
      render: (value: string | null) => value || '— (no mobile)',
    },
  ];

  const onCheck = async () => {
    clearError();
    setChecked(false);
    setShowAudienceError(true);

    if (isContainerMode) {
      if (!containerId) {
        return;
      }
      const response: any = await dispatch(GetBulkNotificationRecipients({ ContainerId: containerId }));
      if (response?.IsValid) {
        setChecked(true);
      }
      // Container mode is always IsValid per the API contract; a technical failure
      // already surfaced via notification.error inside the thunk.
      return;
    }

    const refs = parseRefs(refsText);
    if (refs.length === 0) {
      return;
    }
    const response: any = await dispatch(GetBulkNotificationRecipients({ RefNumbers: refs }));
    if (response?.IsValid) {
      setChecked(true);
    } else if (response) {
      // All-or-nothing: any unmatched reference blocks the whole check.
      setError(
        `These reference numbers were not found: ${response.InvalidRefNumbers.join(', ')}. Fix them and check again.`
      );
    }
  };

  // Field-specific "required" checks render inline (subjectMissing/emailBodyMissing/
  // smsBodyMissing below each control); this only covers the one check with no single
  // control to attach to.
  const validate = (): string => {
    if (result.ReachableCount < 1) return 'No reachable recipients on the selected channel.';
    return '';
  };

  const onPreview = () => {
    if (!checked) {
      setError('Check recipients before previewing.');
      return;
    }
    setShowFieldErrors(true);
    if ((showEmailFields && (!subject.trim() || !emailBody.trim())) || (showSmsField && !smsBody.trim())) {
      return;
    }
    const validationError = validate();
    setError(validationError);
    if (!validationError) {
      setIsModalOpen(true);
    }
  };

  // 2026-07-02 US-003: real send. The endpoint re-resolves the audience and re-projects
  // channel/fallback server-side (it never trusts the client's recipient list), dedupes,
  // and sends synchronously within the request — so the response already carries the real
  // final Sent/Failed counts, not just an id to poll for later (see
  // BulkNotificationService.Send). "View sent messages" is still where the full
  // per-recipient audit trail for this send lives.
  const onConfirmSend = async () => {
    setIsSending(true);

    const sendRequest: SendRequest = {
      ...(isContainerMode ? { ContainerId: containerId } : { RefNumbers: parseRefs(refsText) }),
      Channel: channel,
      FallbackOn: fallbackOn,
      Subject: subject,
      EmailBody: emailBody,
      SmsBody: smsBody,
      ...(attachmentFileName && attachmentBase64
        ? { AttachmentFileName: attachmentFileName, AttachmentBase64: attachmentBase64 }
        : {}),
    };

    const sendResponse: any = await dispatch(SendBulkNotification(sendRequest));
    setIsSending(false);
    if (!sendResponse?.SendId) {
      // Failure already surfaced via notification.error inside the thunk.
      return;
    }

    setIsModalOpen(false);
    setSendResult({
      status: sendResponse.Status,
      sentCount: sendResponse.SentCount,
      failedCount: sendResponse.FailedCount,
      totalRecipients: sendResponse.TotalRecipients,
    });
    setIsResultModalOpen(true);
    // Audience/compose fields and the checked recipient table are left as-is after a
    // send, so the user can still see exactly what they just sent.
  };

  return (
    <div className="App">
      <div className="bulk-notif-page">
        <Row justify="center" align="middle">
          <h1>Send bulk notifications</h1>
        </Row>

        <Row justify="end" className="bulk-notif-history-link">
          <Typography.Link
            className="bulk-notif-history-link-text"
            onClick={() => history.push(PATHS.BulkNotificationHistory.path)}
          >
            View sent messages
          </Typography.Link>
        </Row>

        <Card title="1. Select recipients" className="card-custom bulk-notif-card">
          <Form layout="vertical">
            <Form.Item label="Get recipients by">
              <Radio.Group
                className="bulk-notif-toggle"
                optionType="button"
                buttonStyle="solid"
                value={mode}
                onChange={(e) => onModeChange(e.target.value)}
              >
                <Radio.Button value={RecipientMode.Container}>Container</Radio.Button>
                <Radio.Button value={RecipientMode.Reference}>Reference number</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Row gutter={16}>
              {isContainerMode ? (
                <Col xs={24}>
                  <Form.Item
                    label="Container"
                    validateStatus={containerMissing ? 'error' : ''}
                    help={containerMissing ? Constants.REQUIRED_FIELD : undefined}
                  >
                    <Select
                      size="large"
                      showSearch
                      allowClear
                      loading={containerState.submitStatus === OperationStatus.pending}
                      placeholder="Select a container"
                      optionFilterProp="children"
                      value={containerId}
                      onChange={(value) => {
                        setContainerId(value || undefined);
                        onAudienceChange();
                      }}
                    >
                      {(containerState.ContainerList || []).map((c) => (
                        <Select.Option key={c.Id} value={c.Id}>
                          {c.ContainerNo}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              ) : (
                <Col xs={24}>
                  <Form.Item
                    label="Reference numbers"
                    validateStatus={refsMissing ? 'error' : ''}
                    help={refsMissing ? Constants.REQUIRED_FIELD : undefined}
                  >
                    <Input.TextArea
                      rows={4}
                      placeholder={'Enter reference numbers separated by commas or new lines\ne.g. AU000001, AU000002'}
                      value={refsText}
                      onChange={(e) => {
                        setRefsText(e.target.value);
                        onAudienceChange();
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>

            {!checked && error && <p className="bulk-notif-error">{error}</p>}

            <Row justify="end" className="bulk-notif-actions">
              <Space>
                <Button size="large" onClick={() => history.push(PATHS.Home.path)}>
                  Back
                </Button>
                <Button
                  size="large"
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={bulkState.submitStatus === OperationStatus.pending}
                  onClick={onCheck}
                >
                  Check recipients
                </Button>
              </Space>
            </Row>

            {checked && (
              <div className="bulk-notif-results">
                <Text className="bulk-notif-count">
                  {result.ReachableCount} reachable recipient(s)
                  {result.Unreachable.length > 0 && `, ${result.Unreachable.length} unreachable`}
                </Text>
                <p className="bulk-notif-dedupe-note">
                  Note: If the same email or mobile number appears more than once above, only one message is sent to it
                  — not one per row.
                </p>
                {result.Rows.length > 0 && (
                  <Table
                    dataSource={result.Rows}
                    columns={columns}
                    rowKey="RefNo"
                    size="middle"
                    rowClassName={(r) =>
                      r.Resolved === ResolvedChannel.Unreachable ? 'bulk-notif-row-unreachable' : ''
                    }
                  />
                )}
              </div>
            )}
          </Form>
        </Card>

        {/* 2026-07-02 US-003: compose only appears after recipients are checked —
            no point composing a message before knowing who it goes to */}
        {checked && (
          <Card title="2. Compose message" className="card-custom bulk-notif-card">
            <Form layout="vertical">
              <Form.Item label="Channel">
                <Radio.Group
                  className="bulk-notif-toggle"
                  optionType="button"
                  buttonStyle="solid"
                  value={channel}
                  onChange={(e) => {
                    setChannel(e.target.value);
                    // Fallback always resets to OFF on channel switch — its meaning
                    // ("the other channel") changes with the channel, so a checked
                    // state from one channel shouldn't silently carry over.
                    setFallbackOn(false);
                    // Attachment is only shown/relevant while email fields are visible —
                    // clear it so it can't linger hidden and reappear confusingly later.
                    setAttachmentFileName(undefined);
                    setAttachmentBase64(undefined);
                    clearError();
                  }}
                >
                  <Radio.Button value={NotifChannel.Email}>Email</Radio.Button>
                  <Radio.Button value={NotifChannel.SMS}>SMS</Radio.Button>
                </Radio.Group>
              </Form.Item>

              <Form.Item>
                <Checkbox
                  checked={fallbackOn}
                  onChange={(e) => {
                    setFallbackOn(e.target.checked);
                    clearError();
                  }}
                >
                  {fallbackLabel}
                </Checkbox>
              </Form.Item>

              {showEmailFields && (
                <>
                  <Form.Item
                    label="Subject"
                    required
                    validateStatus={subjectMissing ? 'error' : ''}
                    help={subjectMissing ? Constants.REQUIRED_FIELD : undefined}
                    extra={<Text type="secondary">{charCountLabel(subject)}</Text>}
                  >
                    <Input
                      size="large"
                      placeholder={DEFAULT_EMAIL_SUBJECT}
                      value={subject}
                      onChange={(e) => {
                        setSubject(e.target.value);
                        clearError();
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label={isEmail ? 'Email message' : 'Email fallback message'}
                    required
                    validateStatus={emailBodyMissing ? 'error' : ''}
                    help={emailBodyMissing ? Constants.REQUIRED_FIELD : undefined}
                    extra={<Text type="secondary">{charCountLabel(emailBody)}</Text>}
                  >
                    <Input.TextArea
                      rows={8}
                      placeholder={DEFAULT_EMAIL_BODY}
                      value={emailBody}
                      onChange={(e) => {
                        setEmailBody(e.target.value);
                        clearError();
                      }}
                    />
                  </Form.Item>
                  <Form.Item label="Attachment (optional, PDF only)">
                    <Upload
                      accept=".pdf"
                      maxCount={1}
                      beforeUpload={(file) => {
                        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
                        if (!isPdf) {
                          setError('Only PDF files can be attached.');
                          return Upload.LIST_IGNORE;
                        }
                        clearError();
                        return false; // keep in the (uncontrolled) fileList, never auto-upload
                      }}
                      onChange={(info) => {
                        const file = info.fileList[0]?.originFileObj;
                        if (!file) {
                          setAttachmentFileName(undefined);
                          setAttachmentBase64(undefined);
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = () => {
                          const base64 = (reader.result as string).split(',')[1];
                          setAttachmentFileName(file.name);
                          setAttachmentBase64(base64);
                        };
                        reader.readAsDataURL(file);
                      }}
                    >
                      <Button icon={<UploadOutlined />}>Attach PDF</Button>
                    </Upload>
                  </Form.Item>
                </>
              )}

              {showSmsField && (
                <Form.Item
                  label={isEmail ? 'SMS fallback message' : 'SMS message'}
                  required
                  validateStatus={smsBodyMissing ? 'error' : ''}
                  help={smsBodyMissing ? Constants.REQUIRED_FIELD : undefined}
                  extra={<Text type="secondary">{charCountLabel(smsBody)}</Text>}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder={DEFAULT_SMS_BODY}
                    value={smsBody}
                    onChange={(e) => {
                      setSmsBody(e.target.value);
                      clearError();
                    }}
                  />
                </Form.Item>
              )}

              {checked && error && <p className="bulk-notif-error">{error}</p>}

              <Row justify="end" className="bulk-notif-actions">
                <Button
                  size="large"
                  type="primary"
                  icon={<SendOutlined />}
                  disabled={result.ReachableCount < 1}
                  onClick={onPreview}
                >
                  Preview and send
                </Button>
              </Row>
            </Form>
          </Card>
        )}
      </div>

      <ConfirmationModal
        isModalOpen={isModalOpen}
        title="Send this message?"
        yesText="Send"
        noText="Cancel"
        onYes={onConfirmSend}
        onNo={() => setIsModalOpen(false)}
        loadingState={isSending}
        className="bulk-notif-confirm-modal"
      >
        <Row>
          <Col xs={24}>
            <Text>This can&apos;t be undone once sent.</Text>
          </Col>
        </Row>
      </ConfirmationModal>

      {sendResult && (
        <ConfirmationModal
          isModalOpen={isResultModalOpen}
          title={sendResult.status === SendStatus.Completed ? 'Sent successfully' : 'Sent with some failures'}
          yesText="OK"
          showNo={false}
          onYes={() => setIsResultModalOpen(false)}
          onNo={() => setIsResultModalOpen(false)}
          className="bulk-notif-confirm-modal"
        >
          <Row>
            <Col xs={24}>
              <Text>
                {sendResult.status === SendStatus.Completed
                  ? `Sent ${sendResult.sentCount} message(s) successfully.`
                  : `Sent ${sendResult.sentCount} of ${sendResult.totalRecipients} message(s) — ${sendResult.failedCount} failed. See "View sent messages" for details.`}
              </Text>
            </Col>
          </Row>
        </ConfirmationModal>
      )}
    </div>
  );
};
